import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";

export type Role = "admin" | "manager" | "sales" | "ops" | "accountant" | "readonly";

interface AuthState {
  session: Session | null;
  user: User | null;
  roles: Role[];
  loading: boolean;
  rolesLoading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  // Vrai tant que les rôles de la session courante ne sont pas connus : évite
  // d'afficher « aucun rôle » (et des listes vides) pendant le chargement.
  const [rolesLoading, setRolesLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    // Identifiant de l'utilisateur dont les rôles sont chargés : on ne recharge
    // que si l'utilisateur change. Un rafraîchissement de jeton ou une mise à
    // jour du profil (USER_UPDATED) ne doit pas faire clignoter tout le CRM.
    let loadedFor: string | null = null;
    async function loadRoles(s: Session | null) {
      if (!s) { loadedFor = null; setRoles([]); setRolesLoading(false); return; }
      if (loadedFor === s.user.id) return;
      loadedFor = s.user.id;
      setRolesLoading(true);
      const { data } = await supabase.from("user_roles").select("role").eq("user_id", s.user.id);
      if (!cancelled && loadedFor === s.user.id) { setRoles((data ?? []).map((r) => r.role as Role)); setRolesLoading(false); }
    }
    supabase.auth.getSession().then(async ({ data }) => {
      setSession(data.session);
      await loadRoles(data.session);
      if (!cancelled) setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => {
      setSession(s);
      void loadRoles(s);
    });
    return () => { cancelled = true; sub.subscription.unsubscribe(); };
  }, []);

  return (
    <AuthContext.Provider value={{ session, user: session?.user ?? null, roles, loading, rolesLoading, signOut: () => supabase.auth.signOut().then(() => undefined) }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth hors de AuthProvider");
  return ctx;
}
